/**
 * @cfleet/api-client - Auth Middleware
 *
 * OpenAPI-fetch middleware for automatic token attachment and refresh handling.
 *
 * Features:
 * - Attaches Bearer token to all authenticated requests
 * - Intercepts 401 responses and attempts token refresh
 * - Handles concurrent requests during refresh (request queuing)
 * - Multi-tenant support via X-Tenant-ID header
 * - Configurable callbacks for auth failure and token refresh
 *
 * @example
 * import createClient from 'openapi-fetch';
 * import { createAuthMiddleware, setTokens } from '@cfleet/api-client';
 *
 * const client = createClient<paths>({ baseUrl: '/api/v1' });
 * client.use(createAuthMiddleware({
 *   baseUrl: '/api/v1',
 *   onAuthFailure: () => navigate('/login'),
 * }));
 *
 * // After login
 * const { data } = await client.POST('/auth/login', { body: credentials });
 * setTokens(data.token, data.refresh_token, data.expires_in);
 */

import type { Middleware } from 'openapi-fetch';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  getTokenStorage,
} from './token-storage';

/**
 * Token response from the refresh endpoint
 * Supports both dispatcher/customer format (token) and driver format (access_token)
 */
export interface TokenResponse {
  /** JWT access token (dispatcher/customer format) */
  token?: string;
  /** JWT access token (driver format) */
  access_token?: string;
  /** Refresh token for obtaining new access tokens (may not be present if no rotation) */
  refresh_token?: string;
  /** Token expiry in seconds */
  expires_in: number;
  /** Token type (driver format) */
  token_type?: string;
}

/**
 * Configuration for the auth middleware
 */
export interface AuthMiddlewareConfig {
  /**
   * Base URL for the API.
   * Used to construct the refresh endpoint URL.
   */
  baseUrl: string;

  /**
   * Callback when authentication fails (refresh token expired/invalid).
   * Use this to redirect to login or show auth error UI.
   */
  onAuthFailure?: () => void;

  /**
   * Callback when tokens are successfully refreshed.
   * Use this to sync with external auth state (e.g., @cfleet/auth).
   */
  onTokenRefresh?: (tokens: TokenResponse) => void;

  /**
   * Path to the refresh endpoint relative to baseUrl.
   * @default '/auth/refresh'
   */
  refreshPath?: string;

  /**
   * Paths to exclude from 401 interception.
   * Auth endpoints are excluded by default.
   * @default ['/auth/login', '/auth/refresh', '/auth/password/reset']
   */
  excludedPaths?: string[];
}

/**
 * Default paths that should not trigger token refresh on 401
 */
const DEFAULT_EXCLUDED_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/password/reset',
  '/auth/password/reset/confirm',
  '/driver/auth/login',
  '/driver/auth/refresh',
  '/customer/auth/login',
  '/customer/auth/refresh',
];

/**
 * Creates an authentication middleware for openapi-fetch.
 *
 * The middleware:
 * 1. Attaches Bearer token to all requests (if token exists)
 * 2. Attaches X-Tenant-ID header for multi-tenant requests (if set)
 * 3. Intercepts 401 responses and attempts token refresh
 * 4. Retries the original request with new token after successful refresh
 * 5. Clears tokens and calls onAuthFailure if refresh fails
 *
 * @param config - Middleware configuration
 * @returns OpenAPI-fetch middleware
 *
 * @example
 * const client = createClient<paths>({ baseUrl: '/api/v1' });
 * client.use(createAuthMiddleware({
 *   baseUrl: '/api/v1',
 *   onAuthFailure: () => {
 *     window.location.href = '/login';
 *   },
 *   onTokenRefresh: (tokens) => {
 *     // Optionally sync with external state
 *     console.log('Tokens refreshed');
 *   },
 * }));
 */
export function createAuthMiddleware(config: AuthMiddlewareConfig): Middleware {
  const {
    baseUrl,
    onAuthFailure,
    onTokenRefresh,
    refreshPath = '/auth/refresh',
    excludedPaths = DEFAULT_EXCLUDED_PATHS,
  } = config;

  // State for handling concurrent refresh requests
  let isRefreshing = false;
  let refreshPromise: Promise<boolean> | null = null;

  /**
   * Attempt to refresh the access token.
   * Uses a shared promise to prevent multiple concurrent refresh requests.
   */
  async function attemptRefresh(): Promise<boolean> {
    // If already refreshing, wait for the existing refresh to complete
    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    isRefreshing = true;

    refreshPromise = (async () => {
      try {
        const refreshUrl = `${baseUrl}${refreshPath}`;
        const response = await fetch(refreshUrl, {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!response.ok) {
          // Refresh failed - clear tokens and notify
          clearTokens();
          onAuthFailure?.();
          return false;
        }

        const tokens: TokenResponse = await response.json();

        // Handle both token field names (dispatcher/customer use 'token', driver uses 'access_token')
        const accessToken = tokens.token ?? tokens.access_token;

        // Validate response structure - access token is required
        if (!accessToken) {
          clearTokens();
          onAuthFailure?.();
          return false;
        }

        // Get refresh token - may not be present if no rotation
        // If not present, we'll need to rely on the original refresh token
        const newRefreshToken = tokens.refresh_token ?? getRefreshToken();
        if (!newRefreshToken) {
          clearTokens();
          onAuthFailure?.();
          return false;
        }

        // Store new tokens
        setTokens(accessToken, newRefreshToken, tokens.expires_in);

        // Notify callback
        onTokenRefresh?.(tokens);

        return true;
      } catch (error) {
        // Network error or JSON parse error
        clearTokens();
        onAuthFailure?.();
        return false;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  /**
   * Check if a path should be excluded from 401 interception
   */
  function isExcludedPath(url: string): boolean {
    return excludedPaths.some((path) => url.includes(path));
  }

  return {
    /**
     * Attach authorization headers to outgoing requests
     */
    async onRequest({ request }) {
      const token = getAccessToken();

      if (token) {
        request.headers.set('Authorization', `Bearer ${token}`);
      }

      const tenantId = getTokenStorage().getTenantId();
      if (tenantId) {
        request.headers.set('X-Tenant-ID', tenantId);
      }

      return request;
    },

    /**
     * Intercept 401 responses and attempt token refresh
     */
    async onResponse({ request, response }) {
      // Only intercept 401 responses
      if (response.status !== 401) {
        return response;
      }

      // Don't intercept auth endpoints
      if (isExcludedPath(request.url)) {
        return response;
      }

      // Don't attempt refresh if no refresh token
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokens();
        onAuthFailure?.();
        return response;
      }

      // Attempt token refresh
      const refreshed = await attemptRefresh();

      if (!refreshed) {
        // Refresh failed - return original 401 response
        return response;
      }

      // Retry original request with new token
      const newToken = getAccessToken();
      if (!newToken) {
        return response;
      }

      // Clone the original request and update the authorization header
      // Note: Using new Request(request) preserves all properties including
      // method, headers, mode, credentials, etc. from the original request.
      // The headers from onRequest middleware are already applied.
      const retryRequest = new Request(request);
      retryRequest.headers.set('Authorization', `Bearer ${newToken}`);

      // Make the retry request directly using fetch
      // This bypasses openapi-fetch middleware chain but is the recommended
      // pattern per https://github.com/openapi-ts/openapi-typescript/discussions/2089
      return fetch(retryRequest);
    },
  };
}
